const DEFAULT_FIELD_WIDTH = 180
const DEFAULT_FIELD_HEIGHT = 64
const DEFAULT_FONT_SIZE = 14

export const FIELD_STORAGE_OPTIONS = Object.freeze({
  CONSUMER: 'consumer',
  CUSTOM_FIELDS: 'custom_fields',
  WORKFLOW_RESOLVER: 'workflow_resolver',
})

const CORE_FIELD_ROLES = new Set(['full_name', 'email', 'phone'])

const safeNumber = (value, fallback = 0) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

export const parseLayoutConfig = (config) => {
  if (Array.isArray(config)) return config

  if (typeof config === 'string') {
    try {
      const parsed = JSON.parse(config)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  if (config && typeof config === 'object') {
    if (Array.isArray(config.fields)) return config.fields
    if (Array.isArray(config.layoutConfig)) return config.layoutConfig
  }

  return []
}

export const getActiveLayout = (layouts) => {
  if (!Array.isArray(layouts) || layouts.length === 0) return null
  if (layouts.length === 1) return layouts[0]

  return (
    layouts.find(
      (layout) =>
        layout?.active === true ||
        layout?.is_active === true ||
        layout?.layout_product?.active === true ||
        layout?.LayoutProduct?.active === true
    ) || layouts[0]
  )
}

export const inferFieldStorage = (field = {}) => {
  const storage = String(field?.storage || '').trim().toLowerCase()

  if (storage === FIELD_STORAGE_OPTIONS.CONSUMER) return FIELD_STORAGE_OPTIONS.CONSUMER
  if (storage === FIELD_STORAGE_OPTIONS.CUSTOM_FIELDS) {
    return FIELD_STORAGE_OPTIONS.CUSTOM_FIELDS
  }
  if (storage === FIELD_STORAGE_OPTIONS.WORKFLOW_RESOLVER) {
    return FIELD_STORAGE_OPTIONS.WORKFLOW_RESOLVER
  }

  const role = String(field?.role || 'none').trim().toLowerCase()
  return CORE_FIELD_ROLES.has(role)
    ? FIELD_STORAGE_OPTIONS.CONSUMER
    : FIELD_STORAGE_OPTIONS.CUSTOM_FIELDS
}

export const normalizeFieldPosition = (field = {}) => ({
  ...field,
  x: safeNumber(field.x, 0),
  y: safeNumber(field.y, 0),
  width: safeNumber(field.width, DEFAULT_FIELD_WIDTH),
  height: safeNumber(field.height, DEFAULT_FIELD_HEIGHT),
  fontSize: safeNumber(field.fontSize, DEFAULT_FONT_SIZE),
  label: field.label || field.name || 'Campo',
  name: field.name || '',
  role: field.role || 'none',
  storage: inferFieldStorage(field),
  editable: Boolean(field?.editable),
  config: field?.config && typeof field.config === 'object' ? field.config : {},
})

export const resolveLayoutValue = (field, values = {}) => {
  const role = String(field?.role || 'none').trim().toLowerCase()

  if (role === 'full_name') return values?.full_name || ''
  if (role === 'email') return values?.email || ''
  if (role === 'phone') return values?.phone || ''

  if (field?.storage === FIELD_STORAGE_OPTIONS.WORKFLOW_RESOLVER) {
    const workflowResolvers =
      values?.workflowResolvers && typeof values.workflowResolvers === 'object'
        ? values.workflowResolvers
        : values?.workflow && typeof values.workflow === 'object'
          ? values.workflow
          : {}

    const resolverPath = String(field?.resolverKey || field?.name || '').trim()
    if (!resolverPath) return ''

    if (resolverPath in workflowResolvers) {
      return workflowResolvers[resolverPath]
    }

    if (resolverPath.includes('.')) {
      const nestedValue = resolverPath
        .split('.')
        .reduce(
          (acc, key) => (acc && typeof acc === 'object' ? acc[key] : undefined),
          workflowResolvers
        )

      return nestedValue ?? ''
    }

    if (field?.name && field.name in workflowResolvers) {
      return workflowResolvers[field.name]
    }

    return ''
  }

  const customFields =
    values?.custom_fields && typeof values.custom_fields === 'object'
      ? values.custom_fields
      : {}

  if (field?.name && field.name in customFields) {
    return customFields[field.name]
  }

  return field?.name ? values?.[field.name] || '' : ''
}
